import { useEffect, useState } from 'react'

const BOX_FILE_URL = 'https://app.box.com/file/'

/** Tone for a credit's deal status. */
function statusTone(status) {
  const s = String(status || '').trim().toLowerCase()
  if (s === 'signed' || s === 'fully executed') return 'text-emerald-700'
  if (s === 'archive' || s === 'destroy') return 'text-gray-400'
  if (s === 'watching' || s === 'pending') return 'text-amber-700'
  return 'text-gray-700'
}

function FileLinks({ files, label }) {
  if (!Array.isArray(files) || !files.length) return null
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {label ? <span className="text-[10px] uppercase text-gray-400">{label}</span> : null}
      {files.map((f) => (
        <a
          key={f.id}
          href={`${BOX_FILE_URL}${encodeURIComponent(f.id)}`}
          target="_blank"
          rel="noreferrer"
          className="text-[var(--encore-blue)] underline decoration-dotted underline-offset-2 hover:decoration-solid"
          title={f.path || f.name}
        >
          {f.name}
        </a>
      ))}
    </span>
  )
}

/**
 * Song credits for one track, inside an Assets / Creative expand row.
 *
 * Credits are loaded lazily: the list payload does not carry them, so the
 * first expand fetches and the result is handed back to the table via
 * `onLoaded` so a re-expand reuses it.
 */
export default function AssetsCreditsPanel({ trackFolderId, initialCredits, fetchCredits, onLoaded }) {
  const seeded = Array.isArray(initialCredits) ? initialCredits : null
  const [credits, setCredits] = useState(seeded || [])
  const [loading, setLoading] = useState(!seeded)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (seeded || !trackFolderId || typeof fetchCredits !== 'function') {
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchCredits(trackFolderId)
      .then((res) => {
        if (cancelled) return
        const next = res?.credits || []
        setCredits(next)
        onLoaded?.(trackFolderId, next)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.message || String(err))
        setCredits([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // onLoaded is a stable callback from the table; re-running on identity
    // changes would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackFolderId, seeded, fetchCredits])

  if (loading) {
    return <p className="px-3 py-2 text-xs text-gray-500">Loading credits…</p>
  }
  if (error) {
    return <p className="px-3 py-2 text-xs text-rose-700">Credits failed: {error}</p>
  }
  if (!credits.length) {
    return <p className="px-3 py-2 text-xs text-gray-500">No credits recorded for this track.</p>
  }

  return (
    <div className="overflow-x-auto px-3 py-2">
      <table className="w-full border-collapse text-left text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-gray-400">
            <th className="py-1 pr-3 font-medium">Role</th>
            <th className="py-1 pr-3 font-medium">Name</th>
            <th className="py-1 pr-3 font-medium">Status</th>
            <th className="py-1 pr-3 font-medium">Royalty</th>
            <th className="py-1 pr-3 font-medium">Pub Splits</th>
            <th className="py-1 pr-3 font-medium">Advance</th>
            <th className="py-1 pr-3 font-medium">Paid</th>
            <th className="py-1 pr-3 font-medium">Files</th>
          </tr>
        </thead>
        <tbody>
          {credits.map((c) => (
            <tr key={c.id ?? `${c.Role}-${c.Name}`} className="border-t border-gray-100">
              <td className="py-1 pr-3 text-gray-700">{c.Role || '—'}</td>
              <td className="py-1 pr-3 font-medium text-gray-900">{c.Name || '—'}</td>
              <td className={`py-1 pr-3 ${statusTone(c.Status)}`}>{c.Status || '—'}</td>
              <td className="py-1 pr-3 tabular-nums text-gray-700">{c.Royalty || '—'}</td>
              <td className="py-1 pr-3 tabular-nums text-gray-700">{c['Pub Splits %'] || '—'}</td>
              <td className="py-1 pr-3 tabular-nums text-gray-700">
                {c.Advance == null || c.Advance === ''
                  ? '—'
                  : `$${Number(c.Advance).toLocaleString('en-US')}`}
              </td>
              <td className="py-1 pr-3">
                {c['Paid?'] ? (
                  <span className="text-emerald-700">Yes</span>
                ) : (
                  <span className="text-gray-400">No</span>
                )}
              </td>
              <td className="py-1 pr-3">
                <span className="flex flex-wrap items-center gap-2">
                  <FileLinks files={c.Files} />
                  <FileLinks files={c.LabelWaiverFiles} label="waiver" />
                  {!c.Files?.length && !c.LabelWaiverFiles?.length ? (
                    <span className="text-gray-400">—</span>
                  ) : null}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
